/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import {
    IITSGetAllCustomerEcomCartsResponse,
    IITSCustomerEcomCartsSearchCriteria,
    ILAC_AuthToken
} from '../custom-actions/DataServiceEntities.g';
import { lAC_ITSGetAllCustomerEcomCartsAsync } from '../custom-actions/DataActionExtension.g';
import { refreshToken } from '../utils/authService';

/**
 * GetAllCustomerEcomCarts Input Action
 */
export class GetAllCustomerEcomCartsInput implements Msdyn365.IActionInput {
    public customerEcomCartsSearchCriteria: IITSCustomerEcomCartsSearchCriteria;
    public customerId: string;
    public authTokenCookie: ILAC_AuthToken;

    constructor(
        customerEcomCartsSearchCriteria: IITSCustomerEcomCartsSearchCriteria,
        customerId: string = '',
        authTokenCookie: ILAC_AuthToken
    ) {
        this.customerEcomCartsSearchCriteria = customerEcomCartsSearchCriteria;
        this.customerId = customerId;
        this.authTokenCookie = authTokenCookie;
    }

    // A page asks this several times over - one page of order history after another, the header for the
    // order the browser's cart is, the menu for the newest one - so what was asked for has to be part of
    // the key, or whichever call ran first answers all of them.
    public getCacheKey = () => {
        const criteria = this.customerEcomCartsSearchCriteria;

        return [
            'GETALLCUSTOMERECOMCARTS',
            this.customerId,
            criteria.ChannelId,
            criteria.EventId,
            criteria.TableAssigned,
            criteria.Top,
            criteria.Skip,
            criteria.ColumnName,
            criteria.IsDescending
        ].join('_');
    };

    public getCacheObjectType = () => 'GETALLCUSTOMERECOMCARTS';
    public dataCacheType = (): Msdyn365.CacheType => 'request';
}

async function getAllCustomerEcomCartsAsync(
    input: GetAllCustomerEcomCartsInput,
    ctx: Msdyn365.IActionContext
): Promise<IITSGetAllCustomerEcomCartsResponse> {
    const authToken = input.authTokenCookie;

    if (authToken && authToken.IntuitDomeAccountId && authToken.Token && authToken.RefreshToken) {
        try {
            const result = await lAC_ITSGetAllCustomerEcomCartsAsync(
                { callerContext: ctx },
                authToken,
                input.customerId,
                input.customerEcomCartsSearchCriteria
            );
            if (result) {
                return result;
            }
        } catch (error) {
            console.log('Error getting customer ecom carts:', error);

            const actionError = error as {
                data?: {
                    ErrorResourceId?: string;
                };
            };

            const unAuthorized = actionError.data?.ErrorResourceId === '401';
            if (unAuthorized) {
                const refreshedToken = await refreshToken(ctx);
                if (refreshedToken) {
                    try {
                        const result = await lAC_ITSGetAllCustomerEcomCartsAsync(
                            { callerContext: ctx },
                            refreshedToken,
                            input.customerId,
                            input.customerEcomCartsSearchCriteria
                        );
                        if (result) {
                            return result;
                        }
                    } catch (error) {
                        console.log('Error getting customer ecom carts:', error);
                    }
                }
            }
        }
    }

    return { ITSGetAllCustomerSuspendedCart: [] };
}

export default Msdyn365.createObservableDataAction({
    action: <Msdyn365.IAction<IITSGetAllCustomerEcomCartsResponse>>getAllCustomerEcomCartsAsync,
    id: 'GetAllCustomerEcomCarts'
});
